'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  IndianRupee,
  Wallet,
  Building2,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  ChevronRight,
  ArrowDownToLine,
  RefreshCw,
  Info,
  Loader2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import DirectoryTopbar from '@/app/loan-aggregator-directory/components/DirectoryTopbar';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

// ─── Constants ────────────────────────────────────────────────────────────────

const MINIMUM_PAYOUT = 500; // ₹500 minimum threshold

// ─── Types ────────────────────────────────────────────────────────────────────

type PayoutMethod = 'bank_account' | 'upi';
type PayoutStatus = 'pending' | 'processing' | 'completed' | 'rejected';

interface PayoutRequest {
  id: string;
  amount: number;
  method: PayoutMethod;
  account_holder_name: string;
  account_number: string;
  ifsc_code: string;
  bank_name: string;
  upi_id: string;
  status: PayoutStatus;
  notes: string;
  requested_at: string;
}

interface FormState {
  method: PayoutMethod;
  amount: string;
  // Bank
  account_holder_name: string;
  account_number: string;
  ifsc_code: string;
  bank_name: string;
  // UPI
  upi_id: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatINR(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: PayoutStatus }) {
  const config: Record<PayoutStatus, { label: string; icon: React.ReactNode; cls: string }> = {
    pending: {
      label: 'Pending',
      icon: <Clock size={11} />,
      cls: 'bg-amber-50 text-amber-700 border-amber-200',
    },
    processing: {
      label: 'Processing',
      icon: <RefreshCw size={11} className="animate-spin" />,
      cls: 'bg-blue-50 text-blue-700 border-blue-200',
    },
    completed: {
      label: 'Completed',
      icon: <CheckCircle2 size={11} />,
      cls: 'bg-green-50 text-green-700 border-green-200',
    },
    rejected: {
      label: 'Rejected',
      icon: <XCircle size={11} />,
      cls: 'bg-red-50 text-red-600 border-red-200',
    },
  };
  const { label, icon, cls } = config[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
      {icon}
      {label}
    </span>
  );
}

// ─── Transaction History Row ──────────────────────────────────────────────────

function TransactionRow({ tx }: { tx: PayoutRequest }) {
  const methodLabel = tx.method === 'bank_account' ? 'Bank Transfer' : 'UPI';
  const destination =
    tx.method === 'upi'
      ? tx.upi_id
      : tx.account_number
      ? `••••${tx.account_number.slice(-4)}`
      : '—';

  return (
    <div className="flex items-center gap-4 py-4 border-b border-border last:border-0">
      <div
        className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
          tx.method === 'upi' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'
        }`}
      >
        {tx.method === 'upi' ? <Wallet size={16} /> : <Building2 size={16} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">{methodLabel}</p>
        <p className="text-xs text-muted-foreground truncate">{destination}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-bold text-foreground">{formatINR(tx.amount)}</p>
        <p className="text-xs text-muted-foreground">{formatDate(tx.requested_at)}</p>
      </div>
      <StatusBadge status={tx.status} />
    </div>
  );
}

// ─── Confirmation Screen ──────────────────────────────────────────────────────

function ConfirmationView({ amount, method, onDone }: { amount: number; method: PayoutMethod; onDone: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      <div className="w-16 h-16 rounded-full bg-green-50 border-2 border-green-200 flex items-center justify-center mb-5">
        <CheckCircle2 size={32} className="text-green-500" />
      </div>
      <h2 className="text-xl font-extrabold text-foreground mb-2">Payout Requested!</h2>
      <p className="text-muted-foreground text-sm mb-1">
        Your withdrawal of{' '}
        <span className="font-bold text-foreground">{formatINR(amount)}</span> via{' '}
        <span className="font-semibold">{method === 'upi' ? 'UPI' : 'Bank Transfer'}</span> has been submitted.
      </p>
      <p className="text-xs text-muted-foreground mb-8">
        Payouts are typically processed within 2–5 business days.
      </p>
      <button
        onClick={onDone}
        className="px-6 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors"
      >
        Back to Payouts
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PayoutsPage() {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const supabase = createClient();

  const [availableBalance, setAvailableBalance] = useState(0);
  const [transactions, setTransactions] = useState<PayoutRequest[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedAmount, setSubmittedAmount] = useState(0);
  const [submittedMethod, setSubmittedMethod] = useState<PayoutMethod>('bank_account');

  const [form, setForm] = useState<FormState>({
    method: 'bank_account',
    amount: '',
    account_holder_name: '',
    account_number: '',
    ifsc_code: '',
    bank_name: '',
    upi_id: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  // ── Auth guard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!authLoading && !user) {
      router.push('/');
    }
  }, [user, authLoading, router]);

  // ── Load data ───────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!user) return;
    setLoadingData(true);
    try {
      // Calculate available balance from referral conversions minus paid-out amounts
      const { data: conversions } = await supabase
        .from('user_referral_conversions')
        .select('estimated_earning')
        .eq('user_id', user.id);

      const totalEarned = (conversions ?? []).reduce(
        (sum: number, c: { estimated_earning: number }) => sum + Number(c.estimated_earning),
        0
      );

      // Sum completed + pending + processing payouts (already requested)
      const { data: payouts } = await supabase
        .from('payout_requests')
        .select('*')
        .eq('user_id', user.id)
        .order('requested_at', { ascending: false });

      const requestedTotal = (payouts ?? [])
        .filter((p: PayoutRequest) => p.status !== 'rejected')
        .reduce((sum: number, p: PayoutRequest) => sum + Number(p.amount), 0);

      setAvailableBalance(Math.max(0, totalEarned - requestedTotal));
      setTransactions(payouts ?? []);
    } catch {
      toast.error('Failed to load payout data');
    } finally {
      setLoadingData(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  // ── Form helpers ────────────────────────────────────────────────────────────
  const setField = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};
    const amt = parseFloat(form.amount);

    if (!form.amount || isNaN(amt) || amt <= 0) {
      newErrors.amount = 'Enter a valid amount';
    } else if (amt < MINIMUM_PAYOUT) {
      newErrors.amount = `Minimum payout is ${formatINR(MINIMUM_PAYOUT)}`;
    } else if (amt > availableBalance) {
      newErrors.amount = `Exceeds available balance of ${formatINR(availableBalance)}`;
    }

    if (form.method === 'bank_account') {
      if (!form.account_holder_name.trim()) newErrors.account_holder_name = 'Account holder name is required';
      if (!form.account_number.trim()) newErrors.account_number = 'Account number is required';
      if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc_code.toUpperCase()))
        newErrors.ifsc_code = 'Enter a valid IFSC code (e.g. SBIN0001234)';
      if (!form.bank_name.trim()) newErrors.bank_name = 'Bank name is required';
    } else {
      if (!form.upi_id.trim()) {
        newErrors.upi_id = 'UPI ID is required';
      } else if (!/^[\w.\-]+@[\w]+$/.test(form.upi_id)) {
        newErrors.upi_id = 'Enter a valid UPI ID (e.g. name@upi)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !user) return;

    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        user_id: user.id,
        amount: parseFloat(form.amount),
        method: form.method,
        status: 'pending',
      };

      if (form.method === 'bank_account') {
        payload.account_holder_name = form.account_holder_name.trim();
        payload.account_number = form.account_number.trim();
        payload.ifsc_code = form.ifsc_code.toUpperCase().trim();
        payload.bank_name = form.bank_name.trim();
      } else {
        payload.upi_id = form.upi_id.trim();
      }

      const { error } = await supabase.from('payout_requests').insert(payload);
      if (error) throw error;

      setSubmittedAmount(parseFloat(form.amount));
      setSubmittedMethod(form.method);
      setSubmitted(true);

      // Reset form
      setForm({
        method: 'bank_account',
        amount: '',
        account_holder_name: '',
        account_number: '',
        ifsc_code: '',
        bank_name: '',
        upi_id: '',
      });
    } catch (err: unknown) {
      toast.error((err as Error)?.message ?? 'Failed to submit payout request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    setSubmitted(false);
    loadData();
  };

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-primary" />
      </div>
    );
  }

  const amountNum = parseFloat(form.amount) || 0;
  const belowThreshold = amountNum > 0 && amountNum < MINIMUM_PAYOUT;
  const exceedsBalance = amountNum > availableBalance;

  return (
    <div className="min-h-screen bg-background">
      <DirectoryTopbar />

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <button onClick={() => router.push('/referrals')} className="hover:text-foreground transition-colors">
              Referrals
            </button>
            <ChevronRight size={12} />
            <span className="text-foreground font-medium">Payouts</span>
          </div>
          <h1 className="text-2xl font-extrabold text-foreground tracking-tight">Withdraw Earnings</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Transfer your referral earnings to your bank account or UPI wallet.
          </p>
        </div>

        {/* Balance Card */}
        <div className="bg-gradient-to-br from-primary to-blue-700 rounded-2xl p-6 mb-6 text-white">
          <div className="flex items-center gap-2 mb-1 opacity-80">
            <IndianRupee size={14} />
            <span className="text-sm font-medium">Available Balance</span>
          </div>
          {loadingData ? (
            <div className="h-9 w-32 bg-white/20 rounded-lg animate-pulse mt-1" />
          ) : (
            <p className="text-3xl font-extrabold tracking-tight">{formatINR(availableBalance)}</p>
          )}
          <div className="mt-3 flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2 text-xs">
            <Info size={12} className="flex-shrink-0 opacity-80" />
            <span className="opacity-90">
              Minimum payout threshold: <span className="font-bold">{formatINR(MINIMUM_PAYOUT)}</span>
            </span>
          </div>
        </div>

        {/* Payout Form or Confirmation */}
        <div className="bg-card rounded-2xl border border-border overflow-hidden mb-6">
          {submitted ? (
            <ConfirmationView amount={submittedAmount} method={submittedMethod} onDone={handleDone} />
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Method Selector */}
              <div className="p-5 border-b border-border">
                <p className="text-sm font-semibold text-foreground mb-3">Payout Method</p>
                <div className="grid grid-cols-2 gap-3">
                  {(
                    [
                      { value: 'bank_account', label: 'Bank Account', icon: <Building2 size={18} /> },
                      { value: 'upi', label: 'UPI Wallet', icon: <Wallet size={18} /> },
                    ] as { value: PayoutMethod; label: string; icon: React.ReactNode }[]
                  ).map(({ value, label, icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setField('method', value)}
                      className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 transition-all text-left ${
                        form.method === value
                          ? 'border-primary bg-primary/5 text-primary' :'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                      }`}
                    >
                      <span className={form.method === value ? 'text-primary' : 'text-muted-foreground'}>
                        {icon}
                      </span>
                      <span className="text-sm font-semibold">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Amount */}
              <div className="p-5 border-b border-border">
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Withdrawal Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground font-bold">
                    ₹
                  </span>
                  <input
                    type="number"
                    min={MINIMUM_PAYOUT}
                    max={availableBalance}
                    step="1"
                    value={form.amount}
                    onChange={(e) => setField('amount', e.target.value)}
                    placeholder={`Min. ₹${MINIMUM_PAYOUT}`}
                    className={`w-full pl-8 pr-4 py-3 rounded-xl border text-sm font-semibold bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${
                      errors.amount ? 'border-red-400' : 'border-border'
                    }`}
                  />
                  {availableBalance > 0 && (
                    <button
                      type="button"
                      onClick={() => setField('amount', String(Math.floor(availableBalance)))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-primary font-semibold hover:underline"
                    >
                      Max
                    </button>
                  )}
                </div>
                {errors.amount && (
                  <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                    <AlertCircle size={11} /> {errors.amount}
                  </p>
                )}
                {belowThreshold && !errors.amount && (
                  <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                    <AlertCircle size={11} /> Minimum payout is {formatINR(MINIMUM_PAYOUT)}
                  </p>
                )}
                {exceedsBalance && !errors.amount && (
                  <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                    <AlertCircle size={11} /> Exceeds available balance
                  </p>
                )}
              </div>

              {/* Bank Account Fields */}
              {form.method === 'bank_account' && (
                <div className="p-5 border-b border-border space-y-4">
                  <p className="text-sm font-semibold text-foreground">Bank Account Details</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                        Account Holder Name
                      </label>
                      <input
                        type="text"
                        value={form.account_holder_name}
                        onChange={(e) => setField('account_holder_name', e.target.value)}
                        placeholder="Full name as per bank"
                        className={`w-full px-3.5 py-2.5 rounded-xl border text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${
                          errors.account_holder_name ? 'border-red-400' : 'border-border'
                        }`}
                      />
                      {errors.account_holder_name && (
                        <p className="text-xs text-red-500 mt-1">{errors.account_holder_name}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                        Bank Name
                      </label>
                      <input
                        type="text"
                        value={form.bank_name}
                        onChange={(e) => setField('bank_name', e.target.value)}
                        placeholder="e.g. State Bank of India"
                        className={`w-full px-3.5 py-2.5 rounded-xl border text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${
                          errors.bank_name ? 'border-red-400' : 'border-border'
                        }`}
                      />
                      {errors.bank_name && (
                        <p className="text-xs text-red-500 mt-1">{errors.bank_name}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                        Account Number
                      </label>
                      <input
                        type="text"
                        value={form.account_number}
                        onChange={(e) => setField('account_number', e.target.value)}
                        placeholder="Enter account number"
                        className={`w-full px-3.5 py-2.5 rounded-xl border text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${
                          errors.account_number ? 'border-red-400' : 'border-border'
                        }`}
                      />
                      {errors.account_number && (
                        <p className="text-xs text-red-500 mt-1">{errors.account_number}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                        IFSC Code
                      </label>
                      <input
                        type="text"
                        value={form.ifsc_code}
                        onChange={(e) => setField('ifsc_code', e.target.value.toUpperCase())}
                        placeholder="e.g. SBIN0001234"
                        maxLength={11}
                        className={`w-full px-3.5 py-2.5 rounded-xl border text-sm font-mono bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${
                          errors.ifsc_code ? 'border-red-400' : 'border-border'
                        }`}
                      />
                      {errors.ifsc_code && (
                        <p className="text-xs text-red-500 mt-1">{errors.ifsc_code}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* UPI Fields */}
              {form.method === 'upi' && (
                <div className="p-5 border-b border-border">
                  <p className="text-sm font-semibold text-foreground mb-3">UPI Details</p>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                      UPI ID
                    </label>
                    <input
                      type="text"
                      value={form.upi_id}
                      onChange={(e) => setField('upi_id', e.target.value)}
                      placeholder="e.g. yourname@upi"
                      className={`w-full px-3.5 py-2.5 rounded-xl border text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${
                        errors.upi_id ? 'border-red-400' : 'border-border'
                      }`}
                    />
                    {errors.upi_id && (
                      <p className="text-xs text-red-500 mt-1">{errors.upi_id}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Accepted formats: name@paytm, name@okaxis, name@ybl, etc.
                    </p>
                  </div>
                </div>
              )}

              {/* Submit */}
              <div className="p-5">
                <button
                  type="submit"
                  disabled={submitting || availableBalance < MINIMUM_PAYOUT}
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-white rounded-xl text-sm font-bold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <ArrowDownToLine size={16} />
                      Request Payout
                    </>
                  )}
                </button>
                {availableBalance < MINIMUM_PAYOUT && availableBalance > 0 && (
                  <p className="text-xs text-center text-muted-foreground mt-2">
                    You need{' '}
                    <span className="font-semibold text-foreground">
                      {formatINR(MINIMUM_PAYOUT - availableBalance)}
                    </span>{' '}
                    more to reach the minimum payout threshold.
                  </p>
                )}
                {availableBalance === 0 && !loadingData && (
                  <p className="text-xs text-center text-muted-foreground mt-2">
                    No earnings available yet. Share your referral links to start earning.
                  </p>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Transaction History */}
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <Clock size={13} className="text-primary" />
              </div>
              <h2 className="text-sm font-semibold text-foreground">Payout History</h2>
            </div>
            {transactions.length > 0 && (
              <span className="text-xs text-muted-foreground">{transactions.length} request{transactions.length !== 1 ? 's' : ''}</span>
            )}
          </div>

          {loadingData ? (
            <div className="p-5 space-y-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-4 animate-pulse">
                  <div className="w-9 h-9 rounded-xl bg-muted flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3.5 bg-muted rounded w-1/3" />
                    <div className="h-3 bg-muted rounded w-1/4" />
                  </div>
                  <div className="space-y-1.5 text-right">
                    <div className="h-3.5 bg-muted rounded w-16" />
                    <div className="h-3 bg-muted rounded w-12" />
                  </div>
                  <div className="h-5 bg-muted rounded-full w-16" />
                </div>
              ))}
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-6">
              <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mb-3">
                <ArrowDownToLine size={20} className="text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground mb-1">No payouts yet</p>
              <p className="text-xs text-muted-foreground">
                Your payout requests will appear here once submitted.
              </p>
            </div>
          ) : (
            <div className="px-5">
              {transactions.map((tx) => (
                <TransactionRow key={tx.id} tx={tx} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
